import React, { useState, useEffect } from 'react';
import {
    Box, Heading, VStack, HStack, Text, Card, CardBody,
    Button, FormControl, FormLabel, Select, Input,
    Divider, useToast, Icon
} from '@chakra-ui/react';
import { FiDownload, FiFileText, FiCoffee } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { filterClassesForStaffUser } from '../utils/teacherClassAccess';
import { useAuth } from '../context/AuthContext';

const ReportsPage = () => {
    const { user } = useAuth();
    const toast = useToast();
    const [classes, setClasses] = useState([]);

    // 訂餐報表 state
    const [lunchClassId, setLunchClassId] = useState('');
    const [lunchYear, setLunchYear] = useState(new Date().getFullYear());
    const [lunchMonth, setLunchMonth] = useState(new Date().getMonth() + 1);
    const [downloadingLunch, setDownloadingLunch] = useState(false);

    // 出勤報表 state
    const [attClassId, setAttClassId] = useState('');
    const today = new Date().toISOString().split('T')[0];
    const firstOfMonth = `${today.slice(0, 7)}-01`;
    const [attStart, setAttStart] = useState(firstOfMonth);
    const [attEnd, setAttEnd] = useState(today);
    const [downloadingAtt, setDownloadingAtt] = useState(false);

    useEffect(() => {
        if (!user) return;
        authAPI.getClassesLite().then(res => {
            const filtered = filterClassesForStaffUser(res.data, user);
            setClasses(filtered);
            if (filtered.length > 0) {
                setLunchClassId(String(filtered[0].id));
                setAttClassId(String(filtered[0].id));
            }
        }).catch(console.error);
    }, [user]);

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleLunchExport = async () => {
        if (!lunchClassId) return;
        setDownloadingLunch(true);
        try {
            const res = await authAPI.lunch.exportReport(lunchClassId, lunchYear, lunchMonth);
            const className = classes.find(c => String(c.id) === lunchClassId)?.name || lunchClassId;
            downloadBlob(res.data, `訂餐報表_${className}_${lunchYear}${String(lunchMonth).padStart(2, '0')}.xlsx`);
            toast({ title: '訂餐報表下載成功', status: 'success' });
        } catch (e) {
            toast({ title: '下載失敗', status: 'error' });
        } finally { setDownloadingLunch(false); }
    };

    const handleAttExport = async () => {
        if (!attClassId) return;
        setDownloadingAtt(true);
        try {
            const res = await authAPI.getClassAttendanceReport(attClassId, attStart, attEnd);
            const className = classes.find(c => String(c.id) === attClassId)?.name || attClassId;
            downloadBlob(res.data, `出勤報表_${className}_${attStart}_${attEnd}.xlsx`);
            toast({ title: '出勤報表下載成功', status: 'success' });
        } catch (e) {
            toast({ title: '下載失敗', status: 'error' });
        } finally { setDownloadingAtt(false); }
    };

    const ClassSelect = ({ value, onChange }) => (
        <Select value={value} onChange={e => onChange(e.target.value)} bg="white">
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
    );

    return (
        <Box maxW="800px" mx="auto" py={6} px={4}>
            <Heading size="lg" mb={6} color="brand.800">報表輸出</Heading>

            <VStack spacing={6} align="stretch">
                {/* 訂餐報表 */}
                <Card variant="outline">
                    <CardBody>
                        <HStack mb={4}>
                            <Icon as={FiCoffee} color="orange.500" boxSize={5} />
                            <Text fontSize="lg" fontWeight="bold">訂餐報表</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500" mb={4}>
                            匯出指定班級、指定月份的所有訂餐紀錄（Excel 格式）
                        </Text>
                        <VStack spacing={3} align="stretch">
                            <FormControl>
                                <FormLabel>班級</FormLabel>
                                <ClassSelect value={lunchClassId} onChange={setLunchClassId} />
                            </FormControl>
                            <HStack>
                                <FormControl>
                                    <FormLabel>年份</FormLabel>
                                    <Input
                                        type="number" value={lunchYear} bg="white"
                                        onChange={e => setLunchYear(Number(e.target.value))}
                                    />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>月份</FormLabel>
                                    <Select value={lunchMonth} onChange={e => setLunchMonth(Number(e.target.value))} bg="white">
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>{m} 月</option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </HStack>
                            <Button
                                leftIcon={<FiDownload />} colorScheme="orange"
                                isLoading={downloadingLunch} onClick={handleLunchExport}
                                isDisabled={!lunchClassId} alignSelf="flex-start"
                            >
                                下載訂餐報表
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>

                <Divider />

                {/* 出勤報表 */}
                <Card variant="outline">
                    <CardBody>
                        <HStack mb={4}>
                            <Icon as={FiFileText} color="blue.500" boxSize={5} />
                            <Text fontSize="lg" fontWeight="bold">出勤報表</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500" mb={4}>
                            匯出指定班級、指定日期區間的出勤紀錄（Excel 格式）
                        </Text>
                        <VStack spacing={3} align="stretch">
                            <FormControl>
                                <FormLabel>班級</FormLabel>
                                <ClassSelect value={attClassId} onChange={setAttClassId} />
                            </FormControl>
                            <HStack>
                                <FormControl>
                                    <FormLabel>開始日期</FormLabel>
                                    <Input type="date" value={attStart} bg="white"
                                        onChange={e => setAttStart(e.target.value)} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>結束日期</FormLabel>
                                    <Input type="date" value={attEnd} bg="white"
                                        onChange={e => setAttEnd(e.target.value)} />
                                </FormControl>
                            </HStack>
                            <Button
                                leftIcon={<FiDownload />} colorScheme="blue"
                                isLoading={downloadingAtt} onClick={handleAttExport}
                                isDisabled={!attClassId} alignSelf="flex-start"
                            >
                                下載出勤報表
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>
            </VStack>
        </Box>
    );
};

export default ReportsPage;
